import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

export function HistoricalTrendsTab({ gymId, currentDate }) {
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (gymId) {
      fetchTrends();
    }
  }, [gymId, currentDate]);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const params = {
        days: 7,
        end_date: currentDate || new Date().toISOString().split('T')[0]
      };
      const response = await axiosInstance.get(`/gym-trends/${gymId}`, { params });
      setTrendData(response.data);
    } catch (error) {
      console.error('Failed to load historical trends:', error);
      toast.error('Failed to load historical trends');
      setTrendData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!trendData || !trendData.daily_metrics || trendData.daily_metrics.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-semibold">No historical data available</p>
          <p className="text-sm mt-2">No pulse entries found for this gym in the past 7 days</p>
        </div>
      </div>
    );
  }

  // Transform data for Recharts
  const chartData = trendData.daily_metrics.map(day => ({
    date: format(new Date(day.date), 'MMM dd'),
    connectRate: day.connect_rate,
    closures: day.closures,
    appointments: day.appointments
  }));

  const comparisons = trendData.comparisons || {};
  const hasComparisons = comparisons.vs_yesterday !== null || comparisons.vs_last_week !== null;

  // Helper to render comparison card
  const renderComparisonCard = (title, data) => {
    if (!data) {
      return (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-400">-</p>
            <p className="text-xs text-muted-foreground">Not available</p>
          </CardContent>
        </Card>
      );
    }

    const connectRateDelta = data.connect_rate || 0;
    const closuresDelta = data.closures || 0;
    const connectRateIsPositive = connectRateDelta > 0;
    const closuresIsPositive = closuresDelta > 0;

    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Connect Rate</span>
            <div className="flex items-center gap-1">
              {connectRateDelta !== 0 && (
                connectRateIsPositive ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                )
              )}
              <span className={`text-sm font-semibold ${connectRateDelta > 0 ? 'text-green-600' : connectRateDelta < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {connectRateDelta > 0 ? '+' : ''}{connectRateDelta.toFixed(1)}%
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Closures</span>
            <div className="flex items-center gap-1">
              {closuresDelta !== 0 && (
                closuresIsPositive ? (
                  <TrendingUp className="w-4 h-4 text-green-500" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500" />
                )
              )}
              <span className={`text-sm font-semibold ${closuresDelta > 0 ? 'text-green-600' : closuresDelta < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {closuresDelta > 0 ? '+' : ''}{closuresDelta}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Helper to render monthly average card
  const renderMonthlyAvgCard = () => {
    const monthlyAvg = comparisons.monthly_avg;
    if (!monthlyAvg) {
      return (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Monthly Average</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-400">-</p>
            <p className="text-xs text-muted-foreground">Not available</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Monthly Average</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Connect Rate</span>
            <span className="text-sm font-semibold text-blue-600">
              {monthlyAvg.connect_rate?.toFixed(1)}%
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Closures</span>
            <span className="text-sm font-semibold text-blue-600">
              {monthlyAvg.closures?.toFixed(1)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Appointments</span>
            <span className="text-sm font-semibold text-blue-600">
              {monthlyAvg.appointments?.toFixed(1)}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Line Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">7-Day Performance Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="connectRate"
                stroke="#10b981"
                name="Connect Rate %"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="closures"
                stroke="#3b82f6"
                name="Closures"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="appointments"
                stroke="#8b5cf6"
                name="Appointments"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Comparison Cards */}
      {hasComparisons && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {renderComparisonCard('vs Yesterday', comparisons.vs_yesterday)}
          {renderComparisonCard('vs Last Week', comparisons.vs_last_week)}
          {renderMonthlyAvgCard()}
        </div>
      )}

      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Period Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Date Range</p>
              <p className="font-semibold">{trendData.start_date} to {trendData.end_date}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Days</p>
              <p className="font-semibold">{trendData.days} days</p>
            </div>
            <div>
              <p className="text-muted-foreground">Gym</p>
              <p className="font-semibold">{trendData.gym_id}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Data Points</p>
              <p className="font-semibold">{trendData.daily_metrics.length} entries</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
